import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import axios from 'axios'
import toast from 'react-hot-toast'
import { toastEmailCodeResult } from '../utils/mailFeedback'
import { getAuthErrorMessage } from '../utils/authErrors'
import { AuthSplitLayout, AuthLogo } from '../components/Auth/AuthShell'

export default function ForgotPassword() {
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [step, setStep] = useState(1)
  const [loading, setLoading] = useState(false)
  const [countdown, setCountdown] = useState(0)
  const [sent, setSent] = useState(false)
  const [pendingReview, setPendingReview] = useState(false)

  const sendCode = async () => {
    if (!email) return toast.error('Enter your work email')
    try {
      const res = await axios.post('/api/auth/forgot-password', { email })
      toastEmailCodeResult(res, toast)
      setSent(true)
      setCountdown(60)
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) clearInterval(timer)
          return prev - 1
        })
      }, 1000)
    } catch (err) {
      toast.error(getAuthErrorMessage(err, 'reset', 'Could not send reset code. Please try again.'))
    }
  }

  const handleReset = async (e) => {
    e.preventDefault()
    setLoading(true)
    try {
      const res = await axios.post('/api/auth/reset-password', { email: email.trim().toLowerCase(), code, newPassword })
      setPendingReview(!!res.data?.pendingReview)
      toast.success('Password reset successful!')
      setStep(3)
    } catch (err) {
      toast.error(getAuthErrorMessage(err, 'reset', 'Password reset failed. Please check your code and try again.'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthSplitLayout
      title={
        <>
          Connect Global Capital
          <br />
          with China Assets
        </>
      }
      description="RotationStreet is the trusted AI platform bridging Chinese fund managers with global investors."
      footer={
        <div className="auth-footer-link">
          <Link to="/login">← Back to Login</Link>
        </div>
      }
    >
      <AuthLogo />
      {step === 3 ? (
        <>
          <div className="auth-verify-icon">✓</div>
          <h2 className="auth-verify-title">Password Reset Successful</h2>
          <p className="auth-verify-sub">
            {pendingReview
              ? 'Your password has been updated. Your account is still waiting for administrator approval — you can sign in only after approval. We will notify you by email.'
              : 'Your password has been updated. You can now sign in with your new password.'}
          </p>
          <Link to="/login" className="auth-btn auth-btn-primary" style={{ display: 'block', textAlign: 'center', textDecoration: 'none' }}>
            Go to Login
          </Link>
        </>
      ) : (
        <>
          <div className="auth-subtitle">Reset your password</div>
          <p className="auth-pref-box-desc" style={{ marginBottom: 28 }}>
            Enter your work email address and we&apos;ll send you a verification code to reset your
            password.
          </p>

          {sent && (
            <div className="auth-success-banner">
              If an account exists for this email, a reset code has been sent.
            </div>
          )}

          <form onSubmit={handleReset}>
            <div className="auth-form-group">
              <label className="auth-label">Email Address</label>
              <input
                type="email"
                className="auth-input"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            {!sent ? (
              <button type="button" className="auth-btn auth-btn-primary" onClick={sendCode}>
                Send Reset Code
              </button>
            ) : (
              <>
                <div className="auth-form-group">
                  <label className="auth-label">Verification Code</label>
                  <input
                    type="text"
                    className="auth-input auth-input--code"
                    placeholder="000000"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    maxLength={6}
                    required
                  />
                </div>
                <div className="auth-form-group">
                  <label className="auth-label">New Password</label>
                  <input
                    type="password"
                    className="auth-input"
                    placeholder="Min 8 characters"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    minLength={8}
                    required
                  />
                </div>
                <div className="auth-resend-row">
                  <span className="auth-sublabel" style={{ margin: 0 }}>
                    Didn&apos;t receive the code?
                  </span>
                  <button
                    type="button"
                    className="auth-link"
                    style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                    onClick={sendCode}
                    disabled={countdown > 0}
                  >
                    {countdown > 0 ? `Resend in ${countdown}s` : 'Resend code'}
                  </button>
                </div>
                <button type="submit" className="auth-btn auth-btn-primary" disabled={loading}>
                  {loading ? 'Resetting…' : 'Reset Password'}
                </button>
              </>
            )}
          </form>
        </>
      )}
    </AuthSplitLayout>
  )
}
